import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Bookmark, History, CheckCircle2 } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const BrainSaveButton = ({ clinikoId, data, onSaved, saved }) => {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data: rec } = await api.post(`/clinical-brain/${clinikoId}/save`, { suggestions: data });
      toast.success(rec.cliniko_note ? "Saved to history + draft note in Cliniko" : "Saved to history (Cliniko note not created)");
      onSaved(rec);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed — retry"); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" onClick={save} disabled={busy || saved} data-testid="clinical-brain-save-btn" className="bg-teal-600 hover:bg-teal-700">
      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : saved ? <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> : <Bookmark className="mr-1.5 h-3.5 w-3.5" />}
      {saved ? "Saved" : "Save suggestions"}
    </Button>
  );
};

export const useBrainHistory = (clinikoId) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    if (!clinikoId) { setItems([]); return; }
    api.get(`/clinical-brain/${clinikoId}/saved`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [clinikoId]);
  useEffect(() => { load(); }, [load]);
  return [items, load];
};

export const BrainHistory = ({ items, onOpen }) => {
  if (!items.length) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3" data-testid="clinical-brain-history">
      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><History className="h-4 w-4 text-indigo-600" /> Saved suggestions</div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.id}>
            <button onClick={() => onOpen(it)} data-testid={`brain-history-${it.id}`} className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-indigo-50">
              <span className="truncate text-slate-700">{it.suggestions?.likely_diagnosis || "Suggestions"}</span>
              <span className="ml-2 flex-shrink-0 text-slate-400">{fmt(it.created_at)}{it.cliniko_note ? " · in Cliniko" : ""}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
